import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { trans } from '@spotahome/bff/client';

// lodash utils
import uniq from 'lodash/uniq';
import isEmpty from 'lodash/isEmpty';
import Tooltip from 'rc-tooltip';

import Checked from '@sru/components/icons/Checked';
import InformationIcon from '@sru/components/InformationIcon';
import InstantBooking from '@sru/components/icons/InstantBooking';

import CurrencyFormatter from '@utils/CurrencyFormatter';

import CheckBox from '../../../ui/form/form-checkbox';
import GroupToggle from '../../../ui/form/form-group-toggle';
import ShowApartmentNumber from '../../../../../common/components/ShowApartmentNumber';

import FilterGroup from '../filter-group';

import { getCityAreas } from './../../../../../pages/search/utils/areaConfig';
import AreaFilters from './AreaFilters';

import NUMBER_ROOMS_TOGGLE from './rooms-config';
import NUMBER_BATHROOMS_TOGGLE from './bathrooms-config';
import BED_TYPE_TOGGLE from './beds-config';
import FEATURES from './features-config';
import RULES from './rules-config';
import BILLS from './bills-config';
import BaseFilters from './BaseFilters';

const SHOW_PROPERTIES_WITH_INSTANT_BOOKING = 1;
const SHOW_ALL_PROPERTIES = 0;
const SHOW_VERIFIED_PROPERTIES = 1;

const CONTENT_LIST_TRANS_KEYS = [
  'search.filter.property.tooltip.whole_apartment',
  'search.filter.property.tooltip.studio',
  'search.filter.property.tooltip.rooms',
  'search.filter.property.tooltip.residence'
];

const isValueChecked = (arr = [], value) => arr.includes(value);

class Filters extends PureComponent {
  static defaultProps = {
    simpleFilters: true
  };

  getSelectedTopologies() {
    return this.props.filters['topology[]'].join(',');
  }

  getSelectedBathrooms() {
    return this.props.filters['bathrooms[]'].join(',');
  }

  handleToggleCheckbox(name, value, isChecked) {
    let options = this.props.filters[name];

    if (isChecked) {
      options = [...options, value];
    } else {
      options = options.filter(option => option !== value);
    }

    this.props.updateFiltersWith({
      [name]: uniq(options)
    });
  }

  handleToggleFilterFeaturesCheckbox(value) {
    return isChecked => {
      this.handleToggleCheckbox('features[]', value, isChecked);
    };
  }

  handleToggleFilterTypeCheckbox(value) {
    return isChecked => {
      this.handleToggleCheckbox('type[]', value, isChecked);
    };
  }

  handleApartmentsFilterTypeCheckbox(value) {
    return isChecked => {
      this.handleToggleCheckbox('type[]', value, isChecked);
    };
  }

  handleInstantBookingFilterCheckbox = isChecked =>
    this.props.updateFiltersWith({
      instantBooking: isChecked
        ? SHOW_PROPERTIES_WITH_INSTANT_BOOKING
        : SHOW_ALL_PROPERTIES
    });

  handleAreaFilterTypeCheckbox = value => isChecked =>
    this.handleToggleCheckbox('area[]', value, isChecked);

  isInstantBookingChecked() {
    const instantBooking = parseInt(this.props.filters.instantBooking, 10);
    return instantBooking === SHOW_PROPERTIES_WITH_INSTANT_BOOKING;
  }

  isFeatureChecked(value) {
    return isValueChecked(this.props.filters['features[]'], value);
  }

  isPropertyTypeChecked(value) {
    return isValueChecked(this.props.filters['type[]'], value);
  }

  handleTopologyClick = (name, value, isChecked) =>
    this.handleToggleCheckbox('topology[]', value, isChecked);

  handleBathroomClick = (name, value, isChecked) =>
    this.handleToggleCheckbox('bathrooms[]', value, isChecked);

  handleBedTypesClick = (name, value, isChecked) => {
    let selectedValues = [];
    if (!isEmpty(this.props.filters.bed)) {
      selectedValues = this.props.filters.bed.split(',');
    }

    if (isChecked) {
      selectedValues.push(value);
      selectedValues = uniq(selectedValues);
    } else {
      selectedValues = selectedValues.filter(
        currentVal => currentVal !== value
      );
    }

    this.props.updateFiltersWith({ bed: selectedValues.join(',') });
  };

  handleVerifiedFilterCheckbox = isChecked =>
    this.props.updateFiltersWith({
      verified: isChecked ? SHOW_VERIFIED_PROPERTIES : SHOW_ALL_PROPERTIES
    });

  isVerifiedChecked() {
    const verified = parseInt(this.props.filters.verified, 10);
    return verified === SHOW_VERIFIED_PROPERTIES;
  }

  isAreaIdFilterChecked = value =>
    isValueChecked(this.props.filters['areaId[]'], value);

  handleAreaFilterTypeCheckbox = value => isChecked =>
    this.handleToggleCheckbox('areaId[]', value, isChecked);

  renderBaseFilter = () => (
    <BaseFilters
      currency={this.props.currency}
      filters={this.props.filters}
      maxPriceInCity={this.props.maxPriceInCity}
      minimumStaying={this.props.minimumStaying}
      simpleFilters={this.props.simpleFilters}
      unitPrices={this.props.unitPrices}
      updateFiltersWith={this.props.updateFiltersWith}
    />
  );

  renderPropertyTypeTooltip = () => (
    <Tooltip
      arrowContent={<div className="rc-tooltip-arrow-inner" />}
      overlay={() => (
        <ul className="tooltyp-content__list">
          {CONTENT_LIST_TRANS_KEYS.map(transKey => (
            <li
              key={transKey}
              dangerouslySetInnerHTML={{ __html: trans(transKey) }}
            />
          ))}
        </ul>
      )}
      placement="top"
      trigger={['hover', 'click']}
    >
      <span className="filter-group__filter-icon">
        <InformationIcon size={24} />
      </span>
    </Tooltip>
  );

  renderPropertyTypeFilters = () => (
    <FilterGroup
      classModifier="property-type-checkboxes"
      label={trans('search.filter.property.type')}
      tooltip={this.renderPropertyTypeTooltip}
    >
      <CheckBox
        id="apartment-id"
        label={trans('search.filter.apartments')}
        onChange={this.handleApartmentsFilterTypeCheckbox('apartments')}
        checked={this.isPropertyTypeChecked('apartments')}
        tracking="ga-search-form-apartments"
      />
      {this.isPropertyTypeChecked('apartments') && (
        <div>
          <ShowApartmentNumber number={this.props.filters['topology[]']} />
          <GroupToggle
            onChange={this.handleTopologyClick}
            selectedValues={this.getSelectedTopologies()}
            options={NUMBER_ROOMS_TOGGLE}
            key="specific-key-room"
          />
        </div>
      )}
      <CheckBox
        id="studios-id"
        label={trans('search.filter.studios')}
        onChange={this.handleToggleFilterTypeCheckbox('studios')}
        checked={this.isPropertyTypeChecked('studios')}
        tracking="ga-search-form-studios"
      />
      <CheckBox
        id="rooms-id"
        label={trans('search.filter.rooms')}
        onChange={this.handleToggleFilterTypeCheckbox('rooms')}
        checked={this.isPropertyTypeChecked('rooms')}
        tracking="ga-search-form-rooms"
      />
      <CheckBox
        id="residences-id"
        label={trans('search.filter.residences')}
        onChange={this.handleToggleFilterTypeCheckbox('residences')}
        checked={this.isPropertyTypeChecked('residences')}
        tracking="ga-search-form-residences"
      />
    </FilterGroup>
  );

  renderCityAreasFilter = areaCities => (
    <AreaFilters
      cityAreas={areaCities}
      isAreaIdFilterChecked={this.isAreaIdFilterChecked}
      handleAreaFilterTypeCheckbox={this.handleAreaFilterTypeCheckbox}
    />
  );

  renderInstantBookingFilter = () => (
    <FilterGroup
      label={trans('search.filter.instant-booking.title')}
      tooltip={() => (
        <span className="filter-group__filter-icon">
          <InstantBooking />
        </span>
      )}
    >
      <CheckBox
        id="instantBooking"
        label={trans('search.filter.instant-booking.text')}
        onChange={this.handleInstantBookingFilterCheckbox}
        tracking="ga-search-form-instant-booking"
        checked={this.isInstantBookingChecked()}
      />
    </FilterGroup>
  );

  renderVerifiedFilter = () => (
    <FilterGroup
      label={trans('search.filter.verified.title')}
      tooltip={() => (
        <span className="filter-group__filter-icon">
          <Checked />
        </span>
      )}
    >
      <CheckBox
        id="verified"
        label={trans('search.filter.verified.text')}
        onChange={this.handleVerifiedFilterCheckbox}
        tracking="ga-search-form-verified"
        checked={this.isVerifiedChecked()}
      />
    </FilterGroup>
  );

  renderBedTypeFilter = () => (
    <FilterGroup classModifier="toggle" label={trans('search.filter.type_bed')}>
      <GroupToggle
        onChange={this.handleBedTypesClick}
        selectedValues={this.props.filters.bed}
        options={BED_TYPE_TOGGLE}
        key="specific-key-bed"
      />
    </FilterGroup>
  );

  renderBathroomFilter = () => (
    <FilterGroup
      classModifier="toggle"
      label={trans('search.filter.bathrooms')}
    >
      <GroupToggle
        onChange={this.handleBathroomClick}
        selectedValues={this.getSelectedBathrooms()}
        options={NUMBER_BATHROOMS_TOGGLE}
        key="specific-key-bathrooms"
      />
    </FilterGroup>
  );

  renderFeatureFilter = () => (
    <FilterGroup
      classModifier="checkboxes"
      label={trans('search.filter.features')}
    >
      {FEATURES.map(({ name, labelKey }) => (
        <CheckBox
          key={name}
          id={`${name}-id`}
          label={trans(labelKey)}
          onChange={this.handleToggleFilterFeaturesCheckbox(name)}
          checked={this.isFeatureChecked(name)}
          tracking={`ga-search-form-${name}`}
        />
      ))}
    </FilterGroup>
  );

  renderHouseRulesFilter = () => (
    <FilterGroup
      classModifier="checkboxes"
      label={trans('search.filter.rules')}
    >
      {RULES.map(({ name, labelKey, tracking }) => (
        <CheckBox
          key={name}
          id={`${name}-id`}
          label={trans(labelKey)}
          onChange={this.handleToggleFilterFeaturesCheckbox(name)}
          checked={this.isFeatureChecked(name)}
          tracking={tracking}
        />
      ))}
    </FilterGroup>
  );

  renderIncludedBillsFilter = () => (
    <FilterGroup
      classModifier="checkboxes"
      label={trans('search.filter.bills')}
    >
      {BILLS.map(({ name, labelKey, tracking }) => (
        <CheckBox
          key={name}
          id={`${name}-id`}
          label={trans(labelKey)}
          onChange={this.handleToggleFilterFeaturesCheckbox(name)}
          checked={this.isFeatureChecked(name)}
          tracking={tracking}
        />
      ))}
    </FilterGroup>
  );

  render() {
    const areaCities = getCityAreas(this.props.cityName);

    return (
      <React.Fragment>
        {this.renderBaseFilter()}
        {!this.props.simpleFilters && (
          <React.Fragment>
            {this.renderPropertyTypeFilters()}
            {areaCities.length > 0 && this.renderCityAreasFilter(areaCities)}
            {this.props.hasInstantBooking && this.renderInstantBookingFilter()}
            {this.renderVerifiedFilter()}
            {this.renderBedTypeFilter()}
            {this.renderBathroomFilter()}
            {this.renderFeatureFilter()}
            {this.renderHouseRulesFilter()}
            {this.renderIncludedBillsFilter()}
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}

const filterShape = {
  'bathrooms[]': PropTypes.array,
  bed: PropTypes.string,
  budget: PropTypes.string,
  'features[]': PropTypes.array,
  flexibleMoveIn: PropTypes.string,
  flexibleMoveOut: PropTypes.string,
  instantBooking: 0,
  'move-in': PropTypes.string,
  'move-out': PropTypes.string,
  page: PropTypes.number,
  poi: PropTypes.string,
  sortBy: PropTypes.string,
  'topology[]': PropTypes.array,
  'type[]': PropTypes.array,
  verified: PropTypes.number,
  'areaId[]': PropTypes.array
};

Filters.defaultProps = {
  filters: {
    'bathrooms[]': [],
    bed: '',
    budget: '',
    'features[]': [],
    instantBooking: 0,
    'move-in': '',
    'move-out': '',
    page: 1,
    poi: '',
    sortBy: 'bestMatch',
    'topology[]': [],
    'type[]': [],
    verified: 0,
    'areaId[]': []
  },
  updateFiltersWith: () => {}
};

Filters.propTypes = {
  currency: PropTypes.instanceOf(CurrencyFormatter).isRequired,
  filters: PropTypes.shape(filterShape),
  hasInstantBooking: PropTypes.bool,
  maxPriceInCity: PropTypes.number.isRequired,
  minimumStaying: PropTypes.number.isRequired,
  simpleFilters: PropTypes.bool,
  unitPrices: PropTypes.arrayOf(PropTypes.number).isRequired,
  updateFiltersWith: PropTypes.func,
  cityName: PropTypes.string.isRequired
};

Filters.defaultProps = {
  hasInstantBooking: false
};

export default Filters;
